import { ConversionIntent } from './types';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('CONVERSION_FLOW');

const CONVERSION_STORAGE_KEY = 'pendingConversion';

export function storeConversionIntent(intent: ConversionIntent): void {
  try {
    const serializable = {
      type: intent.type,
      fileName: intent.file.name,
      fileSize: intent.file.size,
      fileType: intent.file.type,
      options: intent.options,
      timestamp: intent.timestamp,
    };
    localStorage.setItem(CONVERSION_STORAGE_KEY, JSON.stringify(serializable));
    log.log('[storeConversionIntent] Stored conversion intent:', serializable);
  } catch (error) {
    log.error('[storeConversionIntent] Failed to store conversion intent:', error);
  }
}

export function getConversionIntent(): any | null {
  try {
    const stored = localStorage.getItem(CONVERSION_STORAGE_KEY);
    if (stored) {
      const intent = JSON.parse(stored);
      log.log('[getConversionIntent] Retrieved conversion intent:', intent);
      return intent;
    }
  } catch (error) {
    log.error('[getConversionIntent] Failed to retrieve conversion intent:', error);
  }
  return null;
}

export function clearConversionIntent(): void {
  try {
    localStorage.removeItem(CONVERSION_STORAGE_KEY);
    log.log('[clearConversionIntent] Cleared conversion intent');
  } catch (error) {
    log.error('[clearConversionIntent] Failed to clear conversion intent:', error);
  }
}

export interface ConversionResult {
  docId: string;
  spaceId: string;
  success: boolean;
  error?: string;
}

export async function processFileConversion(
  file: File,
  authToken: string,
  spaceId: string,
  options: any = {}
): Promise<ConversionResult> {
  log.log('[processFileConversion] Starting conversion:', {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    spaceId,
    options,
  });

  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('spaceId', spaceId);
    
    if (options.ocrEnabled !== undefined) {
      formData.append('ocrEnabled', String(options.ocrEnabled));
    }
    if (options.preserveLayout !== undefined) {
      formData.append('preserveLayout', String(options.preserveLayout));
    }

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';
    
    log.log('[processFileConversion] Uploading file to /api/files/add...');
    const response = await fetch(`${API_BASE_URL}/api/files/add`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      log.error('[processFileConversion] Upload failed:', errorText);
      throw new Error(`File upload failed: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    log.log('[processFileConversion] File uploaded successfully:', result);

    const docId = result._id || result.id;

    if (!docId) {
      throw new Error('No document ID returned from server');
    }

    return {
      docId,
      spaceId,
      success: true,
    };
  } catch (error) {
    log.error('[processFileConversion] Conversion failed:', error);
    return {
      docId: '',
      spaceId,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function handlePostSignupConversion(
  file: File,
  authToken: string,
  spaceId: string,
  options: any = {}
): Promise<ConversionResult> {
  log.log('[handlePostSignupConversion] Processing post-signup conversion...');
  
  const result = await processFileConversion(file, authToken, spaceId, options);
  
  if (result.success) {
    clearConversionIntent();
    log.log('[handlePostSignupConversion] Conversion completed successfully');
  } else {
    log.error('[handlePostSignupConversion] Conversion failed:', result.error);
  }
  
  return result;
}

